import {type MutableRefObject, useCallback, useEffect, useState} from 'react';

import {getCellElements, tableCellUpdateElementAt} from '../lib/cell-elements';
import {beginDrag} from '../lib/pointer-drag';
import type {TableCorner} from '../lib/table-grip';
import {scaleTableConfig, tableNaturalSize} from '../lib/table-scale';
import {
  DEFAULT_COL_WIDTH,
  DEFAULT_ROW_HEIGHT,
  MIN_COL_WIDTH,
  MIN_ROW_HEIGHT,
  type TableColumnData,
  type TableConfig,
  type TableConfigSnapshot,
  type TableRowData,
} from '../lib/table-types';

interface UseTableResizeParams {
  /** Latest committed table config (kept current by the caller's sync effect). */
  configRef: MutableRefObject<TableConfigSnapshot>;
  /** Build a full TableConfig from the current snapshot + overrides. */
  toCfg: (overrides: Partial<TableConfig>) => TableConfig;
  /** Persist a new config to the element. */
  update: (cfg: TableConfig) => void;
  firstRowCellRefs: MutableRefObject<Map<number, HTMLTableCellElement>>;
  rowRefs: MutableRefObject<Map<number, HTMLTableRowElement>>;
}

/** Which side of a row or column a drag grabbed. The table's outer top and left
 *  lines are the first row's and first column's own far edges, so they size the
 *  same part — the sign is the only difference. */
type EdgeSide = 'near' | 'far';

const signOf = (side: EdgeSide) => (side === 'near' ? -1 : 1);

/** The four whole-table corners, and which way each one means "bigger". */
const CORNER_SIGN = {
  nw: {x: -1, y: -1, cursor: 'nwse-resize'},
  ne: {x: 1, y: -1, cursor: 'nesw-resize'},
  sw: {x: -1, y: 1, cursor: 'nesw-resize'},
  se: {x: 1, y: 1, cursor: 'nwse-resize'},
} as const;

/** The sizes a set of columns or rows carries, defaults filled in. They take the
 *  array rather than the config around it: every caller already holds the array,
 *  and asking for a config meant rebuilding one just to read a field back out. */
const widthsOf = (columns: TableColumnData[]) => columns.map((c) => c.width ?? DEFAULT_COL_WIDTH);
const heightsOf = (rows: TableRowData[]) => rows.map((r) => r.height ?? DEFAULT_ROW_HEIGHT);
const sum = (ns: number[]) => ns.reduce((a, b) => a + b, 0);

/**
 * All table drag/resize interactions (column, row, whole-table, cell-element
 * resize, and cell-element reorder-drag). Owns the transient "draft" sizing
 * state the render reads for a live preview; the gesture itself — cursor,
 * listeners, and the rule that a press which never moved commits nothing —
 * belongs to `beginDrag`.
 */
export function useTableResize({
  configRef,
  toCfg,
  update,
  firstRowCellRefs,
  rowRefs,
}: UseTableResizeParams) {
  /**
   * The height a row actually rendered at, for storing on release.
   *
   * The browser has already floored the row at its content height — a `<tr>` height
   * is a minimum — so measuring the DOM while the draft style is still applied snaps
   * a below-minimum drag back to the real minimum, and the stored height never lies
   * about the layout. Where there is no layout at all (jsdom, a hidden table)
   * offsetHeight reads 0, so the caller's drag value stays the fallback.
   */
  const renderedRowHeight = useCallback(
    (ri: number, fallback: number) => rowRefs.current.get(ri)?.offsetHeight || fallback,
    [rowRefs],
  );

  /**
   * The width a column actually rendered at, or the stored one.
   *
   * `||`, not `??`: a table that has not been laid out reports `offsetWidth: 0`,
   * and `0 ?? stored` is 0 — so a drag started there took every column to be
   * zero wide and wrote zeros back on release.
   */
  const renderedColWidth = useCallback(
    (ci: number, fallback: number) => firstRowCellRefs.current.get(ci)?.offsetWidth || fallback,
    [firstRowCellRefs],
  );

  const [draftWidths, setDraftWidths] = useState<number[] | null>(null);
  const [draftHeights, setDraftHeights] = useState<number[] | null>(null);
  const [draftTableWidth, setDraftTableWidth] = useState<number | null>(null);
  const [draftElementSize, setDraftElementSize] = useState<{
    ri: number;
    ci: number;
    elementIdx: number;
    h: number;
    w?: number;
  } | null>(null);

  /**
   * The table as it is drawn right now: stored config, with every column and row
   * replaced by what it actually measures.
   *
   * Measured rather than stored because a stored row height is a MINIMUM — the
   * browser floors each row at its content — so the stored numbers can be
   * shorter than the table on screen, and a drag that started from them would
   * jump on the first move.
   */
  const measuredColumns = useCallback(
    (): TableColumnData[] =>
      configRef.current.columns.map((col, ci) => ({
        ...col,
        width: renderedColWidth(ci, col.width ?? DEFAULT_COL_WIDTH),
      })),
    [configRef, renderedColWidth],
  );

  /** Also what a row carries on COMMIT, not just at the start: the browser has
   *  already floored it, so re-measuring is how a below-content drag stores the
   *  height it actually painted. */
  const measuredRows = useCallback(
    (rows: TableRowData[]): TableRowData[] =>
      rows.map((row, ri) => ({
        ...row,
        height: renderedRowHeight(ri, row.height ?? DEFAULT_ROW_HEIGHT),
      })),
    [renderedRowHeight],
  );

  // ── Resize: one column, by either of its vertical edges ──────────────────────
  /**
   * A line between two columns is the right-hand edge of the one on its left,
   * and moving an edge moves that edge: the table gets wider or narrower and
   * every other column stays exactly where the author put it. (It used to take
   * the width out of the neighbour, so one line moved two columns and the last
   * column — having no neighbour — could not be dragged at all.)
   */
  const startColResize = useCallback(
    (colIdx: number, e: React.MouseEvent, side: EdgeSide = 'far') => {
      const start = measuredColumns();
      const startWidth = widthsOf(start)[colIdx] ?? DEFAULT_COL_WIDTH;
      const minWidth = start[colIdx]?.minWidth ?? MIN_COL_WIDTH;
      let columns = start;

      beginDrag(e, {
        cursor: 'col-resize',
        onMove: (dx) => {
          const width = Math.max(minWidth, startWidth + dx * signOf(side));
          columns = start.map((col, ci) => (ci === colIdx ? {...col, width} : col));
          const widths = widthsOf(columns);
          setDraftWidths(widths);
          // The table's own width follows, or the grid has nowhere to grow into:
          // a fixed `tableWidth` makes the browser redistribute the difference
          // across the other columns, which is the behaviour just removed.
          setDraftTableWidth(sum(widths));
        },
        onSettle: () => {
          setDraftWidths(null);
          setDraftTableWidth(null);
        },
        onCommit: () => update(toCfg({columns, tableWidth: sum(widthsOf(columns))})),
      });
    },
    [measuredColumns, toCfg, update],
  );

  // ── Resize: one row, by either of its horizontal edges ───────────────────────
  const startRowResize = useCallback(
    (rowIdx: number, e: React.MouseEvent, side: EdgeSide = 'far') => {
      const start = measuredRows(configRef.current.rows);
      const startHeight = heightsOf(start)[rowIdx] ?? DEFAULT_ROW_HEIGHT;
      let rows = start;

      beginDrag(e, {
        cursor: 'row-resize',
        onMove: (_dx, dy) => {
          // Canva-style (DERP-226): the drag may request any positive height, but
          // the row can never paint below its content — a <tr> height is a minimum
          // in HTML tables — so the handle visibly stops at the content floor and
          // nothing is ever clipped. The commit stores the rendered (floored) height.
          const height = Math.max(MIN_ROW_HEIGHT, startHeight + dy * signOf(side));
          rows = start.map((row, ri) => (ri === rowIdx ? {...row, height} : row));
          setDraftHeights(heightsOf(rows));
        },
        onSettle: () => setDraftHeights(null),
        onCommit: () => update(toCfg({rows: measuredRows(rows)})),
      });
    },
    [configRef, measuredRows, toCfg, update],
  );

  // ── Resize: photo / logo cell element ────────────────────────────────────────
  const startCellElementResize = useCallback(
    (ri: number, ci: number, elementIdx: number, e: React.MouseEvent) => {
      const cell = configRef.current.rows[ri]?.cells[ci];
      const el = getCellElements(cell ?? {id: '', content: ''})[elementIdx];
      if (!el || (el.type !== 'photo' && el.type !== 'logo')) return;

      const initH = el.config.height ?? (el.type === 'logo' ? 40 : 80);
      const initW = el.type === 'logo' ? (el.config.width ?? 80) : undefined;
      const MIN_DIM = 20;
      let size = {h: initH, w: initW};

      beginDrag(e, {
        cursor: 'nwse-resize',
        onMove: (dx, dy) => {
          size = {
            h: Math.max(MIN_DIM, initH + dy),
            w: initW === undefined ? undefined : Math.max(MIN_DIM, initW + dx),
          };
          setDraftElementSize({ri, ci, elementIdx, ...size});
        },
        onSettle: () => setDraftElementSize(null),
        onCommit: () =>
          update(
            tableCellUpdateElementAt(toCfg({}), ri, ci, elementIdx, {
              ...el,
              config: {
                ...el.config,
                height: size.h,
                ...(size.w !== undefined ? {width: size.w} : {}),
              },
            }),
          ),
      });
    },
    [configRef, toCfg, update],
  );

  // ── Resize: the whole table, from a corner ───────────────────────────────────
  /**
   * The only gesture that scales the grid: every column keeps its share of the
   * width and every row its share of the height, so a 2:1 pair is still 2:1
   * afterwards. The four edges do the opposite — each sizes the one row or
   * column it borders — and keeping the two apart is the point of the split.
   *
   * The arithmetic is `scaleTableConfig`'s, which is the same one a template's
   * stored geometry is scaled by and is tested without a mouse. The table always
   * grows from its top-left, which is where a flow table is anchored and has
   * nowhere else to go; a corner's sign only says which way is "bigger".
   */
  const startOuterResize = useCallback(
    (corner: TableCorner, e: React.MouseEvent) => {
      // The one path that wants a whole config, because `scaleTableConfig` does.
      const base = toCfg({columns: measuredColumns(), rows: measuredRows(configRef.current.rows)});
      const {width: fromWidth, height: fromHeight} = tableNaturalSize(base);
      const minW = MIN_COL_WIDTH * base.columns.length;
      const sign = CORNER_SIGN[corner];
      let scaled = base;

      beginDrag(e, {
        cursor: sign.cursor,
        onMove: (dx, dy) => {
          scaled = scaleTableConfig(base, {
            fromWidth,
            toWidth: Math.max(minW, fromWidth + dx * sign.x),
            fromHeight,
            // No floor on the total beyond the per-row one: each row is already
            // floored at its own content by the browser, and the commit stores
            // what actually rendered.
            toHeight: Math.max(MIN_ROW_HEIGHT, fromHeight + dy * sign.y),
          });
          setDraftWidths(widthsOf(scaled.columns));
          setDraftHeights(heightsOf(scaled.rows));
          setDraftTableWidth(tableNaturalSize(scaled).width);
        },
        onSettle: () => {
          setDraftWidths(null);
          setDraftHeights(null);
          setDraftTableWidth(null);
        },
        onCommit: () =>
          update(
            toCfg({
              columns: scaled.columns,
              rows: measuredRows(scaled.rows),
              tableWidth: tableNaturalSize(scaled).width,
            }),
          ),
      });
    },
    [configRef, measuredColumns, measuredRows, toCfg, update],
  );

  // Reset any lingering body cursor/user-select on unmount.
  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  return {
    draftWidths,
    draftHeights,
    draftTableWidth,
    draftElementSize,
    startColResize,
    startRowResize,
    startCellElementResize,
    startOuterResize,
  };
}
